import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ToolbarIconButton } from './ToolbarIconButton';
import { FormatMenu } from './FormatMenu';
import { PageToolbarProps } from './types';
import { LayoutMenu } from './LayoutMenu';
import TableMenu from './TableMenu';
import { Divider } from './shared/Divider';
import GalleryMenu from './GalleryMenu';
import LinkButton from './LinkButton';
import { LD_DOCUMENTS_WIP_DOCUMENT_BUILDER } from 'constants/featureFlags';

// #region icons and styled-components
const ToolbarWrapper = styled.div`
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1000;
  margin-bottom: var(--cc_size_spacing_xl);
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  padding: var(--cc_size_spacing_s);
  background-color: var(--cc_color_background_1);
  border-radius: var(--cc_size_border_radius_l);
  box-shadow: 0px var(--cc_size_depth_2_y) 20px var(--cc_color_depth_2);
  border: 1px solid var(--cc_color_border_default);
  justify-content: center;
  align-items: center;
`;
// #endregion

const PageToolbar = (props: PageToolbarProps) => {
  const { editor, onChooseGalleryPhoto, className } = props;
  const { [LD_DOCUMENTS_WIP_DOCUMENT_BUILDER]: wipFlag } = useFlags();

  if (!editor) {
    return null;
  }

  const isWithinCoverPageNode = editor.isActive('coverPage');
  const undoEdit = () => editor.commands.undo();
  const redoEdit = () => editor.commands.redo();
  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();
  const toggleTaskList = () => editor.chain().focus().toggleTaskList().run();
  const toggleBulletList = () =>
    editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () =>
    editor.chain().focus().toggleOrderedList().run();

  return (
    <ToolbarWrapper data-testid="page-modal__toolbar" className={className}>
      <ButtonWrapper>
        <FormatMenu editor={editor} />
        <Divider />
        <ToolbarIconButton
          icon="format-bold"
          accessibilityLabel="Text Bold"
          onClick={toggleBold}
          active={editor.isActive('bold')}
        />
        <ToolbarIconButton
          icon="format-italic"
          accessibilityLabel="Text Italic"
          onClick={toggleItalic}
          active={editor.isActive('italic')}
        />
        <ToolbarIconButton
          icon="format-underline"
          accessibilityLabel="Text Underline"
          onClick={toggleUnderline}
          active={editor.isActive('underline')}
        />
        <ToolbarIconButton
          icon="check-circle-outline"
          accessibilityLabel="Checkbox"
          onClick={toggleTaskList}
          active={editor.isActive('taskList')}
        />
        <ToolbarIconButton
          icon="format-list-bulleted"
          accessibilityLabel="Bulleted List"
          onClick={toggleBulletList}
          active={editor.isActive('bulletList')}
        />
        <ToolbarIconButton
          icon="format-list-numbered"
          accessibilityLabel="Numbered List"
          onClick={toggleOrderedList}
          active={editor.isActive('orderedList')}
        />
        <Divider />
        <LinkButton editor={editor} />
        <GalleryMenu
          onChooseGalleryPhoto={onChooseGalleryPhoto}
          editor={editor}
          disabled={isWithinCoverPageNode}
        />
        <TableMenu editor={editor} disabled={isWithinCoverPageNode} />
        {wipFlag ? <LayoutMenu editor={editor} /> : null}
        <Divider />
        <ToolbarIconButton
          icon="undo"
          accessibilityLabel="Undo"
          onClick={undoEdit}
        />
        <ToolbarIconButton
          icon="redo"
          accessibilityLabel="Redo"
          onClick={redoEdit}
        />
      </ButtonWrapper>
    </ToolbarWrapper>
  );
};

export default PageToolbar;
